import { type Action, type ThunkDispatch } from '@reduxjs/toolkit';
import { useMutation } from '@tanstack/react-query';

import { useAppDispatch, useQueryInvalidation } from '@pkg/hooks';

import { type UpdateUsernameParams, type UpdateUserParameters } from '@app/client/common/structs/user/user';
import { FANTASY_LEADERBOARD_CACHE } from '@app/client/resources/competitions';
import { callUpdateCurrentUser, callUpdateUsername } from '@app/client/resources/users';
import { addNotification } from '@app/store/actions/notification';
import { setUser } from '@app/store/actions/user';
import { type RootState } from '@app/store/reducers';

export function useUpdateUser() {
    return useMutation(
        ({ value, dispatch }: { value: UpdateUserParameters; dispatch?: ThunkDispatch<RootState, void, Action> }) =>
            callUpdateCurrentUser({ value }, dispatch).then((r) => {
                if (r.ok && r.value) {
                    dispatch?.(setUser(r.value));
                }
                return r;
            }),
        {
            retry: false,
        },
    );
}

export function useUpdateUsername() {
    const invalidateLeaderboard = useQueryInvalidation(FANTASY_LEADERBOARD_CACHE);
    const dispatch = useAppDispatch();
    return useMutation(
        ({ value }: { value: UpdateUsernameParams }) =>
            callUpdateUsername({ value }).then((r) => {
                if (r.ok && r.value) {
                    dispatch(setUser(r.value));
                    invalidateLeaderboard();

                    dispatch(
                        addNotification({
                            message: 'Username updated!',
                            variant: 'primary',
                            emoji: '💎',
                        }),
                    );
                }
                return r;
            }),
        {
            retry: false,
        },
    );
}
